import { Loading } from "../../app/Status";
import { useQuery } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import {
  CERT_STATE_LABELS,
  COMPANY_DOC_SOURCE_LABELS,
  COMPANY_KIND_LABELS,
  COMPANY_WORK_STATE_LABELS,
  CONTACT_LINK_ROLE_LABELS,
  CONTRACT_STATE_LABELS,
  addDays,
  formatOpeningHours,
  isOpenNow,
  todayIso,
  type CompanyDetailDto,
  type CompanyDocSource,
  type CompanyDocumentDto,
  type CompanyDto,
  type CompanyWorkDto,
  type CompanyWorkItemDto,
  type ContractSummaryDto,
} from "@klantpunt/shared";
import { Tabs } from "../../app/Choice";
import {
  IconAlert,
  IconBuilding,
  IconChevronRight,
  IconContract,
  IconFile,
  IconInspections,
  IconMail,
  IconPhone,
  IconPlus,
  IconRepeat,
  IconShield,
  IconTasks,
} from "../../app/icons";
import { Meta } from "../../app/Meta";
import { buildingPath } from "../../app/navItems";
import { PageHeader } from "../../app/PageHeader";
import { api } from "../../lib/api";
import { hasModule, useMe } from "../../lib/auth";
import { useToast } from "../../lib/toast";
import { termText } from "../contracts/ContractsPage";
import { euro, stateTone, useContracts } from "../contracts/useContracts";
import { fileUrl } from "../dossier/useDossier";
import { Logbook } from "../logbook/Logbook";
import { CertDialog, CompanyDialog, errMsg, MergeDialog, PersonDialog, useDirectoryRefresh } from "./DirectoryDialogs";
import { InsightsTab } from "./InsightsTab";
import { MailDialog } from "./Mail";
import { companyPath, COMPANY_TABS, fmtDate, Phone, type CompanyTab } from "./parts";

export { companyPath };

/** Opzeggen of aflopen binnen zoveel dagen vraagt aandacht (zelfde termijn als de contractsignalen). */
const NOTICE_DAYS = 60;

type Dialog = "edit" | "cert" | "person" | "merge" | "mail" | null;

export function KindChips({ c }: { c: Pick<CompanyDto, "isInternal" | "kinds"> }) {
  if (c.isInternal) return <span className="chip dir-kind dir-internal">Eigen dienst</span>;
  return (
    <>
      {c.kinds.map((k) => (
        <span key={k} className={`chip dir-kind dir-${k}`}>
          {COMPANY_KIND_LABELS[k]}
        </span>
      ))}
    </>
  );
}

/** Werk van het bedrijf (taken, uitvoeringen, keuringen). */
function useCompanyWork(id: string) {
  const { data: me } = useMe();
  return useQuery({
    queryKey: ["company", id, "work"],
    queryFn: () => api<CompanyWorkDto>(`/companies/${id}/work`),
    enabled: hasModule(me, "issues") || hasModule(me, "assets"),
  });
}

/** Contracten met dit bedrijf als leverancier (module Contracten). */
function useCompanyContracts(id: string) {
  const list = useContracts();
  return { ...list, data: list.data?.filter((c) => c.supplier?.id === id) };
}

/** Waar een werkregel naartoe gaat: de taak, of de asset met zijn uitvoeringen. */
function workPath(w: CompanyWorkItemDto) {
  if (w.kind === "task") return `${buildingPath(w.building.id, "taken")}?taak=${w.id}`;
  if (w.asset) return `${buildingPath(w.building.id, "assets")}?asset=${w.asset.id}`;
  return buildingPath(w.building.id, "keuringen");
}

const workTone = (w: CompanyWorkItemDto) =>
  w.state === "late"
    ? "tone-danger"
    : w.state === "done"
      ? w.result === "afgekeurd"
        ? "tone-danger"
        : w.result === "met_opmerkingen"
          ? "tone-warn"
          : "tone-ok"
      : "";

function WorkState({ w }: { w: CompanyWorkItemDto }) {
  const label =
    w.kind === "record" && w.result
      ? { goedgekeurd: "Goedgekeurd", met_opmerkingen: "Met opmerkingen", afgekeurd: "Afgekeurd" }[w.result]
      : COMPANY_WORK_STATE_LABELS[w.state];
  return <span className={`chip tone ${workTone(w)}`.trim()}>{label}</span>;
}

function workIcon(w: CompanyWorkItemDto) {
  if (w.type === "Storing") return <IconAlert size={18} aria-hidden="true" />;
  if (w.kind === "task") return <IconTasks size={18} aria-hidden="true" />;
  return <IconInspections size={18} aria-hidden="true" />;
}

/** Eén regel werk: icoon, titel met soort/gebouw/datum, en de stand. */
function WorkRow({ w }: { w: CompanyWorkItemDto }) {
  const dateLabel =
    w.date &&
    (w.state === "done"
      ? fmtDate(w.date)
      : w.state === "planned"
        ? `volgende keer ${fmtDate(w.date)}`
        : `deadline ${fmtDate(w.date)}`);
  return (
    <li className="co-row">
      <span className="co-ico" aria-hidden="true">
        {workIcon(w)}
      </span>
      <span className="grow">
        <Link to={workPath(w)} className="co-rowtitle">
          {w.title}
        </Link>
        <Meta
          className="small muted"
          items={[
            w.type,
            w.code,
            `${w.building.code} ${w.building.name}`,
            w.asset?.name,
            dateLabel,
            w.openIssues ? `${w.openIssues} ${w.openIssues === 1 ? "bevinding" : "bevindingen"} open` : null,
          ]}
        />
      </span>
      {w.contract && <span className="chip dir-kind co-hide-narrow">{w.contract.name}</span>}
      {w.fileId && (
        <a className="btn small ghost co-hide-narrow" href={fileUrl(w.fileId)} target="_blank" rel="noreferrer">
          Certificaat
        </a>
      )}
      <WorkState w={w} />
    </li>
  );
}

interface Signal {
  tone: "danger" | "warn" | "info";
  icon: ReactNode;
  title: string;
  text: string;
  tab: CompanyTab;
}

/** Wat bij dit bedrijf aandacht vraagt: erkenningen, opzeggen, werk te laat, herhaalde storingen. */
function signalsOf(c: CompanyDetailDto, work: CompanyWorkDto | undefined, contracts: ContractSummaryDto[]): Signal[] {
  const out: Signal[] = [];
  const today = todayIso();
  for (const x of c.certifications) {
    if (x.state === "expired")
      out.push({
        tone: "danger",
        icon: <IconShield size={18} />,
        title: `Erkenning ${x.name} is verlopen`,
        text: x.validUntil ? `Verlopen op ${fmtDate(x.validUntil)}. Vraag een nieuw certificaat op.` : "",
        tab: "documenten",
      });
    else if (x.state === "warn")
      out.push({
        tone: "warn",
        icon: <IconShield size={18} />,
        title: `Erkenning ${x.name} verloopt op ${fmtDate(x.validUntil!)}`,
        text: "Vraag op tijd een nieuw certificaat op.",
        tab: "documenten",
      });
  }
  for (const k of contracts) {
    if (k.state !== "lopend") continue;
    if (k.autoRenew && k.noticeBy && k.noticeBy >= today && k.noticeBy <= addDays(today, NOTICE_DAYS))
      out.push({
        tone: "warn",
        icon: <IconContract size={18} />,
        title: `${k.name}: opzeggen vóór ${fmtDate(k.noticeBy)}`,
        text: "Daarna loopt het contract automatisch door.",
        tab: "contracten",
      });
    else if (!k.autoRenew && k.currentEndOn && k.currentEndOn <= addDays(today, NOTICE_DAYS))
      out.push({
        tone: "warn",
        icon: <IconContract size={18} />,
        title: `${k.name} loopt af op ${fmtDate(k.currentEndOn)}`,
        text: "Verleng of vervang het contract op tijd.",
        tab: "contracten",
      });
  }
  const late = work?.open.filter((w) => w.state === "late") ?? [];
  if (late.length)
    out.push({
      tone: "danger",
      icon: <IconAlert size={18} />,
      title: late.length === 1 ? "1 opdracht te laat" : `${late.length} opdrachten te laat`,
      text: late
        .slice(0, 3)
        .map((w) => w.title)
        .join(", "),
      tab: "werk",
    });
  // Herhaalstoringen: drie of meer storingen bij dezelfde asset in de laatste 12 maanden.
  const yearAgo = addDays(today, -365);
  const storingen = [...(work?.open ?? []), ...(work?.done ?? [])].filter(
    (w) => w.type === "Storing" && (w.date ?? today) >= yearAgo,
  );
  const perTitle = new Map<string, number>();
  for (const w of storingen) perTitle.set(w.building.id, (perTitle.get(w.building.id) ?? 0) + 1);
  for (const [bId, n] of perTitle)
    if (n >= 3) {
      const b = storingen.find((w) => w.building.id === bId)!.building;
      out.push({
        tone: "info",
        icon: <IconRepeat size={18} />,
        title: `${n} storingen in ${b.name} in 12 maanden`,
        text: "Kijk of vervangen goedkoper is dan steeds repareren.",
        tab: "werk",
      });
    }
  return out;
}

function Tile({
  icon,
  label,
  value,
  sub,
  tone,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: "danger" | "warn" | "ok";
  onClick: () => void;
}) {
  return (
    <button type="button" className={`dtile co-tile ${tone ? `tone-${tone}` : ""}`} onClick={onClick}>
      <span className="dtile-head">
        <span className="dtile-ico" aria-hidden="true">
          {icon}
        </span>
        <span className="dtile-label">{label}</span>
        <IconChevronRight size={18} aria-hidden="true" className="dtile-go" />
      </span>
      <span className="dtile-value">{value}</span>
      {sub && <span className="dtile-sub muted small">{sub}</span>}
    </button>
  );
}

/**
 * Bedrijfspagina (premium adresboek, docs/ADRESBOEK.md §10): alles over één bedrijf op één plek, met tabbladen
 * Overzicht, Werk, Contracten, Documenten, Gebouwen, Personen en Logboek. Het tabblad staat in het adres (`?tab=`).
 */
export function CompanyPage() {
  const { companyId = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const refresh = useDirectoryRefresh();
  const { data: me } = useMe();
  const canEdit = hasModule(me, "directory", "edit");
  const seeContracts = hasModule(me, "contracts");
  const seeWork = hasModule(me, "issues") || hasModule(me, "assets");
  const [dialog, setDialog] = useState<Dialog>(null);
  const q = useQuery({
    queryKey: ["company", companyId],
    queryFn: () => api<CompanyDetailDto>(`/companies/${companyId}`),
  });
  const work = useCompanyWork(companyId);
  const contracts = useCompanyContracts(companyId);
  const docs = useQuery({
    queryKey: ["company", companyId, "documents"],
    queryFn: () => api<CompanyDocumentDto[]>(`/companies/${companyId}/documents`),
  });

  if (!hasModule(me, "directory"))
    return (
      <div className="center">
        <p>Geen toegang tot de module Adresboek.</p>
      </div>
    );
  const c = q.data;
  if (!c)
    return (
      <div className="center" aria-busy={!q.isError}>
        {q.isError ? <p className="muted">{errMsg(q.error)}</p> : <Loading />}
      </div>
    );

  const supplier = c.isInternal || c.kinds.includes("supplier");
  const myContracts = contracts.data ?? [];
  const showContracts = seeContracts && (supplier || myContracts.length > 0);
  const seeInsights = canEdit && seeWork && !c.isInternal && c.kinds.includes("supplier");
  const raw = params.get("tab");
  const tab: CompanyTab =
    COMPANY_TABS.includes(raw as CompanyTab) &&
    (raw !== "contracten" || showContracts) &&
    (raw !== "inzichten" || seeInsights)
      ? (raw as CompanyTab)
      : "overzicht";
  const setTab = (v: CompanyTab) => {
    const p = new URLSearchParams(params);
    if (v === "overzicht") p.delete("tab");
    else p.set("tab", v);
    setParams(p, { replace: true });
  };
  const today = todayIso();
  const openWork = work.data?.open ?? [];
  const currentLinks = c.links.filter((l) => l.current);
  const nBuildings = new Set(currentLinks.map((l) => l.building.id)).size;
  const docCount = (docs.data?.length ?? 0) + c.certifications.length;

  const archive = async () => {
    try {
      await api(`/companies/${c.id}/${c.archivedAt ? "unarchive" : "archive"}`, { method: "POST" });
      await refresh();
      toast(c.archivedAt ? "Bedrijf teruggezet" : "Bedrijf gearchiveerd");
    } catch (err) {
      toast(errMsg(err));
    }
  };

  const tabs = (
    <Tabs<CompanyTab>
      label="Onderdeel"
      value={tab}
      onChange={setTab}
      items={[
        { value: "overzicht", label: "Overzicht" },
        ...(seeInsights ? [{ value: "inzichten" as const, label: "Inzichten" }] : []),
        ...(seeWork ? [{ value: "werk" as const, label: "Werk", count: openWork.length || undefined }] : []),
        ...(showContracts
          ? [{ value: "contracten" as const, label: "Contracten", count: myContracts.length || undefined }]
          : []),
        { value: "documenten", label: "Documenten", count: docCount || undefined },
        { value: "gebouwen", label: "Gebouwen", count: nBuildings || undefined },
        { value: "personen", label: "Personen", count: c.people.length || undefined },
        { value: "logboek", label: "Logboek" },
      ]}
    />
  );

  const call = c.emergencyPhone ?? c.phone;
  return (
    <div className="page co-page">
      <PageHeader
        label={<Link to="/adresboek">Adresboek</Link>}
        title={c.name}
        action={
          canEdit &&
          !c.archivedAt &&
          (c.email || c.people.some((p) => p.email)) && (
            <button type="button" className="btn primary" onClick={() => setDialog("mail")}>
              <IconMail aria-hidden="true" /> Mail sturen
            </button>
          )
        }
        menu={
          canEdit && (
            <>
              <button type="button" className="menuitem" onClick={() => setDialog("edit")}>
                Bewerken
              </button>
              <button type="button" className="menuitem" onClick={() => setDialog("person")}>
                Persoon toevoegen
              </button>
              {!c.isInternal && (
                <>
                  <button type="button" className="menuitem" onClick={() => setDialog("cert")}>
                    Erkenning toevoegen
                  </button>
                  <button type="button" className="menuitem" onClick={() => setDialog("merge")}>
                    Samenvoegen met…
                  </button>
                  <button type="button" className="menuitem" onClick={() => void archive()}>
                    {c.archivedAt ? "Terugzetten" : "Archiveren"}
                  </button>
                </>
              )}
            </>
          )
        }
        tabs={tabs}
      />
      <div className="co-sub">
        <KindChips c={c} />
        {c.trades.map((t) => (
          <span key={t} className="chip">
            {t}
          </span>
        ))}
        {c.city && <span className="muted small">{c.city}</span>}
        {c.archivedAt && <span className="chip tone tone-warn">Gearchiveerd</span>}
        {call && (
          <span className="co-quick">
            {c.phone && (
              <a className="btn small" href={`tel:${c.phone.replace(/[^\d+]/g, "")}`}>
                <IconPhone aria-hidden="true" /> Bellen
              </a>
            )}
            {c.emergencyPhone && (
              <a className="btn small" href={`tel:${c.emergencyPhone.replace(/[^\d+]/g, "")}`}>
                <IconAlert aria-hidden="true" /> Storing
              </a>
            )}
          </span>
        )}
      </div>

      {tab === "overzicht" && (
        <Overview
          c={c}
          work={work.data}
          seeWork={seeWork}
          contracts={showContracts ? myContracts : null}
          nBuildings={nBuildings}
          today={today}
          onTab={setTab}
        />
      )}
      {tab === "inzichten" && <InsightsTab companyId={c.id} />}
      {tab === "werk" && <WorkTab work={work.data} pending={work.isPending} />}
      {tab === "contracten" && <ContractsTab contracts={myContracts} pending={contracts.isPending} />}
      {tab === "documenten" && (
        <DocumentsTab
          c={c}
          docs={docs.data}
          pending={docs.isPending}
          canEdit={canEdit}
          onAddCert={() => setDialog("cert")}
        />
      )}
      {tab === "gebouwen" && <BuildingsTab c={c} work={work.data} />}
      {tab === "personen" && <PeopleTab c={c} canEdit={canEdit} onAdd={() => setDialog("person")} />}
      {tab === "logboek" && (
        <div className="panel">
          <Logbook
            entity="company"
            id={c.id}
            path={`/companies/${c.id}/log`}
            canComment={canEdit}
            className="logbook"
          />
        </div>
      )}

      {dialog === "edit" && (
        <CompanyDialog company={c} onClose={() => setDialog(null)} onSaved={() => setDialog(null)} />
      )}
      {dialog === "person" && (
        <PersonDialog companyId={c.id} onClose={() => setDialog(null)} onSaved={() => setDialog(null)} />
      )}
      {dialog === "cert" && <CertDialog companyId={c.id} onClose={() => setDialog(null)} />}
      {dialog === "merge" && (
        <MergeDialog
          company={c}
          onClose={() => setDialog(null)}
          onMerged={(id) => {
            setDialog(null);
            navigate(companyPath(id), { replace: true });
          }}
        />
      )}
      {dialog === "mail" && <MailDialog target={{ companyId: c.id }} links={c.links} onClose={() => setDialog(null)} />}
    </div>
  );
}

function Overview({
  c,
  work,
  seeWork,
  contracts,
  nBuildings,
  today,
  onTab,
}: {
  c: CompanyDetailDto;
  work: CompanyWorkDto | undefined;
  seeWork: boolean;
  contracts: ContractSummaryDto[] | null;
  nBuildings: number;
  today: string;
  onTab: (t: CompanyTab) => void;
}) {
  const signals = signalsOf(c, work, contracts ?? []);
  const open = work?.open ?? [];
  const late = open.filter((w) => w.state === "late").length;
  const running = (contracts ?? []).filter((k) => k.state === "lopend" || k.state === "nog_niet");
  const year = running.reduce((n, k) => n + (k.yearCents ?? 0), 0);
  const hours = formatOpeningHours(c.openingHours);
  const isOpen = isOpenNow(c.openingHours, new Date());
  const certWorst = c.certState;
  const recent = (work?.done ?? []).slice(0, 3);
  return (
    <>
      <div className="dgrid co-tiles">
        {seeWork && (
          <Tile
            icon={<IconTasks size={20} />}
            label="Open werk"
            value={<span className="num">{open.length}</span>}
            sub={late ? `${late} te laat` : open.length ? "Niets te laat" : "Geen open werk"}
            tone={late ? "danger" : undefined}
            onClick={() => onTab("werk")}
          />
        )}
        {contracts && (
          <Tile
            icon={<IconContract size={20} />}
            label="Contracten"
            value={<span className="num">{running.length}</span>}
            sub={year ? `${euro(year)} per jaar` : running.length ? "Lopend" : "Geen lopend contract"}
            tone={signals.some((s) => s.tab === "contracten") ? "warn" : undefined}
            onClick={() => onTab("contracten")}
          />
        )}
        {!c.isInternal && (
          <Tile
            icon={<IconShield size={20} />}
            label="Erkenningen"
            value={<span className="num">{c.certifications.length}</span>}
            sub={certWorst ? CERT_STATE_LABELS[certWorst] : "Geen erkenningen vastgelegd"}
            tone={certWorst === "expired" ? "danger" : certWorst === "warn" ? "warn" : certWorst ? "ok" : undefined}
            onClick={() => onTab("documenten")}
          />
        )}
        <Tile
          icon={<IconBuilding size={20} />}
          label="Gebouwen"
          value={<span className="num">{nBuildings}</span>}
          sub={nBuildings ? "Lopende koppelingen" : "Nog niet gekoppeld"}
          onClick={() => onTab("gebouwen")}
        />
      </div>

      <div className="co-grid">
        <div className="co-col">
          {signals.length > 0 && (
            <section className="panel co-panel" aria-label="Aandacht nodig">
              <div className="co-head">
                <h2>Aandacht nodig</h2>
                <span className="chip tone tone-warn">{signals.length}</span>
              </div>
              <ul className="co-list">
                {signals.map((s) => (
                  <li key={s.title} className="co-row">
                    <span className={`co-ico co-ico-${s.tone}`} aria-hidden="true">
                      {s.icon}
                    </span>
                    <span className="grow">
                      <strong>{s.title}</strong>
                      {s.text && <span className="small muted co-block">{s.text}</span>}
                    </span>
                    <button type="button" className="btn small" onClick={() => onTab(s.tab)}>
                      Bekijken
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
          {seeWork && (
            <section className="panel co-panel" aria-label="Lopend werk">
              <div className="co-head">
                <h2>Lopend werk</h2>
              </div>
              {open.length ? (
                <ul className="co-list">
                  {open.slice(0, 5).map((w) => (
                    <WorkRow key={`${w.kind}-${w.id}`} w={w} />
                  ))}
                </ul>
              ) : (
                <p className="muted small">Geen open taken of uitvoeringen die binnenkort moeten.</p>
              )}
              {open.length > 5 && (
                <button type="button" className="linkbtn" onClick={() => onTab("werk")}>
                  Alle {open.length} bekijken
                </button>
              )}
            </section>
          )}
          {recent.length > 0 && (
            <section className="panel co-panel" aria-label="Laatst afgerond">
              <div className="co-head">
                <h2>Laatst afgerond</h2>
              </div>
              <ul className="co-list">
                {recent.map((w) => (
                  <WorkRow key={`${w.kind}-${w.id}`} w={w} />
                ))}
              </ul>
            </section>
          )}
        </div>
        <div className="co-col">
          <section className="panel co-panel" aria-label="Gegevens">
            <div className="co-head">
              <h2>Gegevens</h2>
            </div>
            {c.emergencyPhone && (
              <div className="dir-emergency co-emergency">
                <IconPhone aria-hidden="true" />
                <span className="grow">
                  <span className="label">Storingsnummer</span>
                  <Phone n={c.emergencyPhone} label="Storingsnummer" />
                </span>
              </div>
            )}
            {hours && (
              <p className="small">
                {isOpen === true ? (
                  <span className="chip tone tone-ok">Nu bereikbaar</span>
                ) : isOpen === false ? (
                  <span className="chip tone tone-warn">Buiten openingstijden</span>
                ) : null}{" "}
                <span className="muted">{hours}</span>
              </p>
            )}
            <dl className="kv">
              {c.phone && (
                <>
                  <dt>Telefoon</dt>
                  <dd>
                    <Phone n={c.phone} />
                  </dd>
                </>
              )}
              {c.email && (
                <>
                  <dt>E-mail</dt>
                  <dd className="dir-wrap">
                    <a href={`mailto:${c.email}`}>{c.email}</a>
                  </dd>
                </>
              )}
              {c.website && (
                <>
                  <dt>Website</dt>
                  <dd className="dir-wrap">{c.website}</dd>
                </>
              )}
              {(c.address || c.city) && (
                <>
                  <dt>Adres</dt>
                  <dd>{[c.address, [c.postalCode, c.city].filter(Boolean).join(" ")].filter(Boolean).join(", ")}</dd>
                </>
              )}
              {c.kvk && (
                <>
                  <dt>KvK</dt>
                  <dd className="num">{c.kvk}</dd>
                </>
              )}
            </dl>
            {c.notes && <p className="small dir-notes">{c.notes}</p>}
            {!c.phone && !c.email && !c.address && !c.emergencyPhone && (
              <p className="muted small">Nog geen contactgegevens. Vul ze aan via ⋯ › Bewerken.</p>
            )}
          </section>
          <section className="panel co-panel" aria-label="Personen">
            <div className="co-head">
              <h2>Personen</h2>
              {c.people.length > 3 && (
                <button type="button" className="btn small ghost" onClick={() => onTab("personen")}>
                  Alle {c.people.length}
                </button>
              )}
            </div>
            {c.people.length ? (
              <ul className="co-list">
                {c.people.slice(0, 3).map((p) => (
                  <li key={p.id} className="co-row">
                    <span className="co-av" aria-hidden="true">
                      {initials(p.name)}
                    </span>
                    <span className="grow">
                      <Link to={`/adresboek?tab=personen&persoon=${p.id}`} className="co-rowtitle">
                        {p.name}
                      </Link>
                      <Meta className="small muted" items={[p.jobTitle, p.availability]} />
                    </span>
                    <Phone n={p.mobile ?? p.phone} label={p.name} />
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted small">Nog geen personen.</p>
            )}
          </section>
          {today && c.links.length > 0 && (
            <section className="panel co-panel" aria-label="Gebouwen">
              <div className="co-head">
                <h2>Gebouwen</h2>
                {nBuildings > 4 && (
                  <button type="button" className="btn small ghost" onClick={() => onTab("gebouwen")}>
                    Alle {nBuildings}
                  </button>
                )}
              </div>
              <ul className="co-list">
                {uniqueBuildings(c)
                  .slice(0, 4)
                  .map(({ link, n }) => (
                    <li key={link.building.id} className="co-row">
                      <span className="co-ico" aria-hidden="true">
                        <IconBuilding size={18} />
                      </span>
                      <span className="grow">
                        <Link to={buildingPath(link.building.id, "overzicht")} className="co-rowtitle">
                          {link.building.name}
                        </Link>
                        <Meta
                          className="small muted"
                          items={[link.building.code, CONTACT_LINK_ROLE_LABELS[link.role], link.trade]}
                        />
                      </span>
                      {n > 0 && <span className="small muted num">{n} open</span>}
                    </li>
                  ))}
              </ul>
            </section>
          )}
        </div>
      </div>
    </>
  );

  function uniqueBuildings(co: CompanyDetailDto) {
    const seen = new Map<string, { link: CompanyDetailDto["links"][number]; n: number }>();
    for (const l of co.links.filter((x) => x.current))
      if (!seen.has(l.building.id))
        seen.set(l.building.id, { link: l, n: open.filter((w) => w.building.id === l.building.id).length });
    return [...seen.values()];
  }
}

const initials = (name: string) =>
  name
    .replace(/\(.*?\)/g, "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("");

function WorkTab({ work, pending }: { work: CompanyWorkDto | undefined; pending: boolean }) {
  const [show, setShow] = useState<"open" | "done">("open");
  if (pending) return <Loading />;
  const rows = (show === "open" ? work?.open : work?.done) ?? [];
  return (
    <section className="panel co-panel" aria-label="Werk">
      <div className="co-head">
        <div className="kpis" role="group" aria-label="Filter">
          <button type="button" className="kpi" aria-pressed={show === "open"} onClick={() => setShow("open")}>
            Open <b className="num">{work?.open.length ?? 0}</b>
          </button>
          <button type="button" className="kpi" aria-pressed={show === "done"} onClick={() => setShow("done")}>
            Afgerond <b className="num">{work?.done.length ?? 0}</b>
          </button>
        </div>
      </div>
      {rows.length ? (
        <ul className="co-list">
          {rows.map((w) => (
            <WorkRow key={`${w.kind}-${w.id}`} w={w} />
          ))}
        </ul>
      ) : (
        <p className="muted small">
          {show === "open"
            ? "Geen open taken, en geen uitvoeringen die de komende zes maanden moeten."
            : "Nog niets afgerond in de laatste twee jaar."}
        </p>
      )}
      <p className="hint">
        Taken en storingen waarin dit bedrijf uitvoerder is, en onderhoud en keuringen die het uitvoert, uit alle
        gebouwen die je ziet.
      </p>
    </section>
  );
}

function ContractsTab({ contracts, pending }: { contracts: ContractSummaryDto[]; pending: boolean }) {
  if (pending) return <Loading />;
  if (!contracts.length)
    return (
      <section className="panel co-panel">
        <p className="muted">
          Nog geen contracten met dit bedrijf. Leg er een vast bij <Link to="/contracten">Contracten</Link>.
        </p>
      </section>
    );
  const order = { lopend: 0, nog_niet: 1, opgezegd: 2, concept: 3, beeindigd: 4 } as const;
  return (
    <div className="co-contracts">
      {[...contracts]
        .sort((a, b) => order[a.state] - order[b.state] || a.name.localeCompare(b.name, "nl"))
        .map((k) => (
          <Link key={k.id} to={`/contracten/${k.id}`} className={`panel co-contract co-${k.state}`}>
            <span className="co-head">
              <span className="grow">
                <strong className="co-rowtitle">{k.name}</strong>
                <Meta
                  className="small muted co-block"
                  items={[
                    k.reference && `nr. ${k.reference}`,
                    k.buildings.length === 1 ? k.buildings[0]!.name : `${k.buildings.length} gebouwen`,
                    k.client.name,
                  ]}
                />
              </span>
              <span className={`chip tone ${stateTone(k)}`.trim()}>
                {k.endsSoon && k.state === "lopend" ? "Loopt af" : CONTRACT_STATE_LABELS[k.state]}
              </span>
            </span>
            <Term k={k} />
            <span className="co-facts">
              <span>
                <b className="num">{k.yearCents != null ? euro(k.yearCents) : "–"}</b>
                <span className="small muted">per jaar</span>
              </span>
              <span>
                <b className="num">{k.lineCount}</b>
                <span className="small muted">{k.lineCount === 1 ? "post" : "posten"}</span>
              </span>
              <span>
                <b className="num">{k.obligationCount}</b>
                <span className="small muted">{k.obligationCount === 1 ? "uitvoering" : "uitvoeringen"}</span>
              </span>
              <span>
                <b>{termText(k)}</b>
                <span className="small muted">looptijd</span>
              </span>
            </span>
          </Link>
        ))}
    </div>
  );
}

/** Tijdbalk van het contract: hoe ver het loopt, met een streep op de opzegdatum. */
function Term({ k }: { k: ContractSummaryDto }) {
  const end = k.currentEndOn;
  if (!end || k.state === "concept") return null;
  const t = (iso: string) => new Date(`${iso}T12:00:00`).getTime();
  const from = t(k.startsOn);
  const span = t(end) - from;
  if (span <= 0) return null;
  const pct = (iso: string) => Math.min(100, Math.max(0, ((t(iso) - from) / span) * 100));
  const now = pct(todayIso());
  return (
    <span className="co-term" aria-hidden="true">
      <span className="co-termbar">
        <i style={{ width: `${now}%` }} />
        {k.noticeBy && k.state === "lopend" && <b style={{ left: `${pct(k.noticeBy)}%` }} />}
      </span>
      <span className="co-termx small muted num">
        <span>{fmtDate(k.startsOn)}</span>
        {k.noticeBy && k.state === "lopend" && <span className="co-notice">opzeggen vóór {fmtDate(k.noticeBy)}</span>}
        <span>{fmtDate(end)}</span>
      </span>
    </span>
  );
}

function DocumentsTab({
  c,
  docs,
  pending,
  canEdit,
  onAddCert,
}: {
  c: CompanyDetailDto;
  docs: CompanyDocumentDto[] | undefined;
  pending: boolean;
  canEdit: boolean;
  onAddCert: () => void;
}) {
  const [q, setQ] = useState("");
  const today = todayIso();
  const soon = addDays(today, NOTICE_DAYS);
  const norm = q.trim().toLocaleLowerCase("nl");
  const hit = (s: string) => !norm || s.toLocaleLowerCase("nl").includes(norm);
  const groups: [CompanyDocSource, CompanyDocumentDto[]][] = (["contract", "certificate", "dossier"] as const).map(
    (s) => [s, (docs ?? []).filter((d) => d.source === s && hit(`${d.title} ${d.detail}`))],
  );
  const certs = c.certifications.filter((x) => hit(`${x.name} ${x.number ?? ""}`));
  const validChip = (until: string | null) =>
    !until ? null : until < today ? (
      <span className="chip tone tone-danger">Verlopen</span>
    ) : until <= soon ? (
      <span className="chip tone tone-warn">Tot {fmtDate(until)}</span>
    ) : null;
  return (
    <section className="panel co-panel" aria-label="Documenten">
      <div className="co-head">
        <input
          type="search"
          className="dir-search"
          aria-label="Zoeken in documenten"
          placeholder="Zoeken in documenten"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        {canEdit && !c.isInternal && (
          <button type="button" className="btn small" onClick={onAddCert}>
            <IconPlus aria-hidden="true" /> Erkenning
          </button>
        )}
      </div>
      {!c.isInternal && (
        <div className="co-docgroup">
          <p className="label">Erkenningen</p>
          {certs.length ? (
            <ul className="co-list">
              {certs.map((x) => (
                <li key={x.id} className="co-row">
                  <span className="co-ico" aria-hidden="true">
                    <IconShield size={18} />
                  </span>
                  <span className="grow">
                    <strong>{x.name}</strong>
                    <Meta
                      className="small muted co-block"
                      items={[x.number, x.validUntil && `geldig tot ${fmtDate(x.validUntil)}`]}
                    />
                  </span>
                  {x.state !== "unknown" && (
                    <span
                      className={`chip tone ${x.state === "expired" ? "tone-danger" : x.state === "warn" ? "tone-warn" : "tone-ok"}`}
                    >
                      {CERT_STATE_LABELS[x.state]}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted small">{norm ? "Geen erkenningen gevonden." : "Geen erkenningen vastgelegd."}</p>
          )}
        </div>
      )}
      {pending ? (
        <Loading />
      ) : (
        groups
          .filter(([, list]) => list.length)
          .map(([source, list]) => (
            <div key={source} className="co-docgroup">
              <p className="label">{COMPANY_DOC_SOURCE_LABELS[source]}</p>
              <ul className="co-list">
                {list.map((d) => (
                  <li key={`${d.source}-${d.id}`} className="co-row">
                    <span className="co-ico" aria-hidden="true">
                      <IconFile size={18} />
                    </span>
                    <span className="grow">
                      <strong className="co-rowtitle">{d.title}</strong>
                      <Meta className="small muted co-block" items={[d.detail, d.date && fmtDate(d.date)]} />
                    </span>
                    {validChip(d.source === "certificate" ? null : d.validUntil)}
                    {d.documentId && d.building && (
                      <Link
                        className="btn small ghost co-hide-narrow"
                        to={`${buildingPath(d.building.id, "dossier")}?document=${d.documentId}`}
                      >
                        In dossier
                      </Link>
                    )}
                    {d.fileId && (
                      <a className="btn small" href={fileUrl(d.fileId)} target="_blank" rel="noreferrer">
                        Openen
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))
      )}
      {!pending && !groups.some(([, l]) => l.length) && !norm && (
        <p className="muted small">Nog geen certificaten, contractdocumenten of dossierstukken van dit bedrijf.</p>
      )}
      <p className="hint">
        Hier komt vanzelf samen wat al in Klantpunt staat: contractdocumenten, certificaten en werkbonnen van keuringen
        die dit bedrijf deed, en dossierdocumenten met dit bedrijf als uitgever.
      </p>
    </section>
  );
}

function BuildingsTab({ c, work }: { c: CompanyDetailDto; work: CompanyWorkDto | undefined }) {
  const [past, setPast] = useState(false);
  const today = todayIso();
  const ended = c.links.filter((l) => !l.current && l.validFrom <= today);
  const rows = c.links.filter((l) => past || l.current || l.validFrom > today);
  const openIn = (id: string) => (work?.open ?? []).filter((w) => w.building.id === id).length;
  return (
    <section className="panel co-panel" aria-label="Gebouwen">
      {rows.length ? (
        <div className="tscroll">
          <table className="it" aria-label="Gebouwen">
            <thead>
              <tr>
                <th scope="col">Gebouw</th>
                <th scope="col">Rol</th>
                <th scope="col" className="co-hide-narrow">
                  Periode
                </th>
                <th scope="col" className="num">
                  Open werk
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((l) => (
                <tr key={l.id} className={!l.current && l.validFrom <= today ? "dir-ended" : undefined}>
                  <td className="tcell-title">
                    <Link to={`${buildingPath(l.building.id, "gebouw")}?tab=contacten`}>
                      <span className="code">{l.building.code}</span> {l.building.name}
                    </Link>
                    {(l.floor || l.room) && (
                      <div className="small muted">{[l.floor?.name, l.room?.name].filter(Boolean).join(" · ")}</div>
                    )}
                  </td>
                  <td>
                    {CONTACT_LINK_ROLE_LABELS[l.role]}
                    {l.isPrimary && " (hoofdcontact)"}
                    {l.trade && <div className="small muted">{l.trade}</div>}
                  </td>
                  <td className="co-hide-narrow small">
                    {l.validTo ? `${fmtDate(l.validFrom)} – ${fmtDate(l.validTo)}` : `Sinds ${fmtDate(l.validFrom)}`}
                  </td>
                  <td className="num">{openIn(l.building.id) || <span className="muted">–</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="muted small">{ended.length ? "Geen lopende koppelingen." : "Nog niet gekoppeld."}</p>
      )}
      {ended.length > 0 && (
        <button type="button" className="linkbtn small" onClick={() => setPast(!past)}>
          {past ? "Eerdere verbergen" : `Eerdere tonen (${ended.length})`}
        </button>
      )}
      <p className="hint">Koppelen doe je bij het gebouw, tabblad Contacten.</p>
    </section>
  );
}

function PeopleTab({ c, canEdit, onAdd }: { c: CompanyDetailDto; canEdit: boolean; onAdd: () => void }) {
  return (
    <>
      {canEdit && (
        <div className="co-actions">
          <button type="button" className="btn small" onClick={onAdd}>
            <IconPlus aria-hidden="true" /> Persoon
          </button>
        </div>
      )}
      {c.people.length ? (
        <div className="co-people">
          {c.people.map((p) => (
            <section key={p.id} className="panel co-person" aria-label={p.name}>
              <span className="co-head">
                <span className="co-av" aria-hidden="true">
                  {initials(p.name)}
                </span>
                <span className="grow">
                  <Link to={`/adresboek?tab=personen&persoon=${p.id}`} className="co-rowtitle">
                    {p.name}
                  </Link>
                  {p.jobTitle && <span className="small muted co-block">{p.jobTitle}</span>}
                </span>
                {p.user && <span className="chip dir-kind">Gebruiker</span>}
              </span>
              <dl className="kv">
                {p.mobile && (
                  <>
                    <dt>Mobiel</dt>
                    <dd>
                      <Phone n={p.mobile} label={p.name} />
                    </dd>
                  </>
                )}
                {p.phone && (
                  <>
                    <dt>Telefoon</dt>
                    <dd>
                      <Phone n={p.phone} label={p.name} />
                    </dd>
                  </>
                )}
                {p.email && (
                  <>
                    <dt>E-mail</dt>
                    <dd className="dir-wrap">
                      <a href={`mailto:${p.email}`}>{p.email}</a>
                    </dd>
                  </>
                )}
                {p.availability && (
                  <>
                    <dt>Bereikbaar</dt>
                    <dd>{p.availability}</dd>
                  </>
                )}
              </dl>
            </section>
          ))}
        </div>
      ) : (
        <section className="panel co-panel">
          <p className="muted small">Nog geen personen bij dit bedrijf.</p>
        </section>
      )}
    </>
  );
}
